"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useFrappeSession } from "./frappe-session";
import { clearMfaContext } from "./mfa-flow";
import { readSignupIdentity, saveSignupIdentity } from "./registration-service";
import { RegistrationError } from "./app-error";
import { toTitleCase } from "./utils";
import { useFacilityService, type LiveFacility, type UserProfile } from "./facility-service";

type SignedInUser = {
  name: string;
  email: string;
  username: string;
  role: string;
  /** Empty when no real name is known (never the username or an ID number). */
  firstName: string;
  /** "professional" accounts get the affiliation banner and no invite actions (F17). */
  kind: UserProfile["role"];
  affiliation: UserProfile["affiliation"];
};

export type FacilityOption = {
  id: string;
  name: string;
  reference: string;
  location: string;
  status: string;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  isReady: boolean;
  user: SignedInUser;
  facilities: FacilityOption[];
  facilitiesReady: boolean;
  activeFacilityId: string;
  setActiveFacilityId: (id: string) => void;
  refreshFacilities: (selectId?: string) => Promise<FacilityOption[]>;
  /** Why affiliation actions (invite, bulk invite) are locked, or null when
      they are available. Drives the RoleBanner and the disabled invite steps. */
  affiliationLock: AffiliationLock;
  /** False until the active facility's affiliation check has answered. */
  affiliationLockReady: boolean;
  /** Re-check the active facility's affiliations (e.g. after an invite). */
  refreshAffiliationLock: () => void;
  signOut: () => void | Promise<void>;
};

export type AffiliationLock =
  | "professional-pending" | "professional-none"
  /** The admin's own affiliation to the facility is not accepted yet: accept it
      in Practice360 (admin is a practitioner) or Facility360 (staff admin). */
  | "admin-pending-p360" | "admin-pending-f360"
  | "facility-pending" | "no-affiliations" | null;

/** Accepted affiliation statuses (active includes Confirmed). */
const ACCEPTED_STATUSES = new Set(["active", "confirmed"]);

const AuthContext = createContext<AuthContextValue | null>(null);
const ACTIVE_FACILITY_KEY = "f360_active_facility";

/** Registry names often arrive in capitals ("JANE WANJIRU"); show them as "Jane Wanjiru". */
const displayCase = (name: string) => name === name.toUpperCase() ? toTitleCase(name.toLowerCase()) : name;

function toOption(facility: LiveFacility): FacilityOption {
  return {
    id: facility.name || facility.hieId,
    name: facility.facilityName || facility.name,
    reference: facility.registrationNumber || facility.hieId,
    location: [facility.subCounty, facility.county].filter(Boolean).join(", "),
    status: facility.operationalStatus || "Active",
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const facilityService = useFacilityService();
  const [isReady, setIsReady] = useState(false);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [facilitiesReady, setFacilitiesReady] = useState(false);
  const [activeFacilityId, setActiveFacilityIdState] = useState("");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [professionalFallback, setProfessionalFallback] = useState<Pick<UserProfile, "role" | "affiliation"> | null>(null);
  /** Active facility's affiliation state: none at all, only pending requests, or at least one accepted. */
  const [facilityAffiliation, setFacilityAffiliation] = useState<"none" | "pending" | "accepted" | null>(null);
  /** The admin's own affiliation to the active facility, when it is not accepted. */
  const [ownPending, setOwnPending] = useState<"p360" | "f360" | null>(null);
  const { currentUser, isLoading: frappeIsLoading, error: frappeError, logout: frappeLogout, updateCurrentUser } = useFrappeSession();

  const sessionAuthenticated = Boolean(currentUser && currentUser !== "Guest" && !frappeError);

  useEffect(() => { setIsReady(true); }, []);

  // Live facilities once the SDK session is known (onboarded one at a time).
  useEffect(() => {
    if (frappeIsLoading) return;
    if (!currentUser || currentUser === "Guest") { setFacilities([]); setFacilitiesReady(true); return; }
    let active = true;
    setIsAdmin(null); setProfessionalFallback(null);
    facilityService.myFacilities()
      .then((rows) => {
        if (!active) return;
        setIsAdmin(true);
        const options = rows.map(toOption).filter((option) => option.id);
        setFacilities(options);
        const saved = window.sessionStorage.getItem(ACTIVE_FACILITY_KEY);
        setActiveFacilityIdState(
          saved && options.some((option) => option.id === saved) ? saved : (options[0]?.id ?? ""),
        );
        setFacilitiesReady(true);
      })
      .catch((error) => {
        if (!active) return;
        // 403 from get_my_facilities_v3: "No administrator profile found for
        // this account" - the user is not a facility administrator.
        if (error instanceof RegistrationError && error.status === 403) setIsAdmin(false);
        setFacilities([]); setFacilitiesReady(true);
      });
    return () => { active = false; };
  }, [frappeIsLoading, currentUser, facilityService]);

  // Non-administrators are treated as health professionals. Until me.profile
  // exists, their affiliation state comes from their own pending requests.
  useEffect(() => {
    if (isAdmin !== false || profile) return;
    let active = true;
    facilityService.myPendingAffiliations().then((result) => {
      if (active) setProfessionalFallback({ role: "professional", affiliation: result?.pending ? "pending" : "none" });
    });
    return () => { active = false; };
  }, [isAdmin, profile, facilityService]);

  // A facility with no accepted affiliation locks affiliation actions and shows
  // the banner: "no affiliations yet", or "pending" while requests wait for the
  // practitioner to accept. Counts come from facilities.details
  // (affiliation_counts: pending, active - active includes Confirmed).
  const [lockCheck, setLockCheck] = useState(0);
  const refreshAffiliationLock = useCallback(() => setLockCheck((value) => value + 1), []);
  useEffect(() => {
    setFacilityAffiliation(null);
    if (!activeFacilityId) return;
    let active = true;
    facilityService.facilityDetails(activeFacilityId)
      .then(({ affiliationCounts }) => {
        if (!active) return;
        setFacilityAffiliation(affiliationCounts.active > 0 ? "accepted" : affiliationCounts.pending > 0 ? "pending" : "none");
      })
      .catch(() => { /* unknown: leave actions available */ });
    return () => { active = false; };
  }, [activeFacilityId, facilityService, lockCheck]);

  // The admin's own affiliation (created at onboarding) must be accepted in
  // the right app before the banner clears.
  useEffect(() => {
    setOwnPending(null);
    if (!activeFacilityId || isAdmin === false || !currentUser || currentUser === "Guest") return;
    let active = true;
    facilityService.myFacilityAffiliation(activeFacilityId, currentUser)
      .then((own) => {
        if (!active || !own || ACCEPTED_STATUSES.has(own.status.toLowerCase())) return;
        setOwnPending(own.isPractitioner ? "p360" : "f360");
      })
      .catch(() => { /* unknown: no banner */ });
    return () => { active = false; };
  }, [activeFacilityId, isAdmin, currentUser, facilityService, lockCheck]);

  // Display name, role and affiliation state (me.profile). Null until the
  // backend ships the operation; the user object below falls back.
  useEffect(() => {
    if (frappeIsLoading || !currentUser || currentUser === "Guest") { setProfile(null); return; }
    let active = true;
    facilityService.myProfile().then((result) => { if (active) setProfile(result); });
    return () => { active = false; };
  }, [frappeIsLoading, currentUser, facilityService]);

  useEffect(() => {
    if (!currentUser || currentUser === "Guest") return;
    const interval = window.setInterval(() => updateCurrentUser(), 60_000);
    return () => window.clearInterval(interval);
  }, [currentUser, updateCurrentUser]);

  const setActiveFacilityId = (id: string) => {
    setActiveFacilityIdState(id);
    window.sessionStorage.setItem(ACTIVE_FACILITY_KEY, id);
  };

  // Refs so refreshFacilities keeps a stable identity: it is used inside
  // effects across the app (setup, onboarding gate). Depending on
  // `activeFacilityId` state recreated it every render, which
  // re-triggered those effects and aborted in-flight detail fetches.
  const activeIdRef = useRef(activeFacilityId);
  activeIdRef.current = activeFacilityId;
  const serviceRef = useRef(facilityService);
  serviceRef.current = facilityService;

  /** Re-read facilities (e.g. after linking another) and return the options. */
  const refreshFacilities = useCallback(async (selectId?: string) => {
    const rows = await serviceRef.current.myFacilities().catch(() => []);
    const options = rows.map(toOption).filter((option) => option.id);
    setFacilities(options);
    if (selectId && options.some((option) => option.id === selectId)) {
      setActiveFacilityIdState(selectId);
      window.sessionStorage.setItem(ACTIVE_FACILITY_KEY, selectId);
    } else if (!options.some((option) => option.id === activeIdRef.current)) {
      setActiveFacilityIdState(options[0]?.id ?? "");
    }
    setFacilitiesReady(true);
    return options;
  }, []);

  // Background linking: picked during guest signup for an already-registered
  // ID, completed automatically once signed in. No verification email - the
  // successful login already proves account ownership.
  const linkAttempted = useRef("");
  useEffect(() => {
    if (frappeIsLoading) return;
    if (!currentUser || currentUser === "Guest") return;
    const pending = readSignupIdentity()?.selectedFacilityId;
    if (!pending || linkAttempted.current === `${currentUser}:${pending}`) return;
    linkAttempted.current = `${currentUser}:${pending}`;
    let active = true;
    (async () => {
      try {
        const result = await facilityService.linkAdditionalFacility(pending);
        const stored = readSignupIdentity();
        if (stored) saveSignupIdentity({ ...stored, selectedFacilityId: undefined });
        if (!active) return;
        if (result.linked) {
          const options = await refreshFacilities();
          if (!active) return;
          const match = options.find((option) => option.reference === result.linked?.registrationNumber);
          if (match) setActiveFacilityId(match.id);
        }
      } catch {
        const stored = readSignupIdentity();
        if (stored) saveSignupIdentity({ ...stored, selectedFacilityId: undefined });
      }
    })();
    return () => { active = false; };
  }, [frappeIsLoading, currentUser, facilityService, refreshFacilities]);

  const signOut = async () => {
    clearMfaContext();
    try {
      await frappeLogout();
    } finally {
      window.location.assign("/sign-in");
    }
  };

  // useFrappeSession owns session discovery and validation (Frappe's
  // get_logged_user). The browser cannot read the HttpOnly sid, and no other
  // session cookie is relayed to it.
  const sessionReady = isReady && !frappeIsLoading;
  const user = useMemo<SignedInUser>(() => {
    const kind = profile?.role ?? professionalFallback?.role ?? "administrator";
    const role = kind === "professional" ? "Health professional" : "Facility administrator";
    const affiliation = profile?.affiliation ?? professionalFallback?.affiliation ?? "accepted";
    if (!currentUser || currentUser === "Guest") {
      return { name: "", firstName: "", email: "", username: "", role, kind, affiliation };
    }
    // Real name from me.profile. An email local part is a last resort; a bare
    // username (often an ID number) is never used as a name.
    const realName = profile?.fullName
      || (currentUser.includes("@") ? toTitleCase(currentUser.split("@")[0].replace(/[._-]+/g, " ")) : "");
    const name = realName ? displayCase(realName) : currentUser;
    const firstName = realName ? (profile?.firstName ? displayCase(profile.firstName) : name.split(/\s+/)[0]) : "";
    return { name, firstName, email: currentUser, username: currentUser, role, kind, affiliation };
  }, [currentUser, profile, professionalFallback]);
  const affiliationLock: AffiliationLock = user.kind === "professional" && user.affiliation !== "accepted"
    ? (user.affiliation === "pending" ? "professional-pending" : "professional-none")
    : ownPending ? `admin-pending-${ownPending}`
    : facilityAffiliation === "pending" ? "facility-pending"
    : facilityAffiliation === "none" ? "no-affiliations" : null;
  const affiliationLockReady = affiliationLock !== null || facilityAffiliation !== null || (facilitiesReady && !activeFacilityId);
  const value = useMemo(() => ({ isAuthenticated: sessionAuthenticated, isReady: sessionReady, user, facilities, facilitiesReady, activeFacilityId, setActiveFacilityId, refreshFacilities, affiliationLock, affiliationLockReady, refreshAffiliationLock, signOut }), [sessionAuthenticated, sessionReady, user, facilities, facilitiesReady, activeFacilityId, refreshFacilities, affiliationLock, affiliationLockReady, refreshAffiliationLock]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function useRequireAuth() {
  const auth = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  useEffect(() => {
    if (auth.isReady && !auth.isAuthenticated && pathname.startsWith("/dashboard")) {
      const nextPath = `${pathname}${typeof window === "undefined" ? "" : window.location.search}`;
      router.replace(`/sign-in?next=${encodeURIComponent(nextPath)}`);
    }
  }, [auth.isAuthenticated, auth.isReady, pathname, router]);
  return auth;
}
